// ============================================
// FILE: src/components/CartScreen.jsx
// ============================================

import React, { useState } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, Tag, X, Sparkles } from 'lucide-react';
import { getProductImage } from '../utils/imageHelpers';

const CartScreen = ({ 
  cart, 
  updateQuantity, 
  removeFromCart, 
  getCartTotal,
  getCartCount,
  handleCheckout,
  // Coupon props
  couponCode,
  setCouponCode,
  appliedCoupon,
  couponDiscount,
  couponError,
  handleApplyCoupon,
  handleRemoveCoupon,
  getFinalTotal,
  getSuggestedCoupons,
  // Settings
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50 }
}) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const suggestedCoupons = getSuggestedCoupons ? getSuggestedCoupons() : [];

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-b-3xl">
        <h1 className="text-2xl font-bold">Shopping Cart</h1>
        <p className="text-blue-100">{getCartCount()} items in cart</p>
      </div>

      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center mt-20 px-6 text-center">
          <ShoppingCart size={64} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">Your cart is empty</h3>
          <p className="text-gray-400">Add some fresh dairy products to get started!</p>
        </div>
      ) : (
        <div className="p-4">
          {cart.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm p-4 mb-3 flex items-center">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg mr-3">
                {getProductImage(item, 'medium')}
              </div>
              
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800">{item.name}</h3>
                {item.sale_price && item.sale_price < item.price ? (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-red-600">₹{item.sale_price} each</p>
                    <p className="text-xs text-gray-400 line-through">₹{item.price}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">₹{item.price} each</p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQuantity(item.id, -1)}
                  className="bg-gray-100 p-1 rounded-lg hover:bg-gray-200"
                >
                  <Minus size={16} className="text-gray-600" />
                </button>
                
                <span className="font-semibold text-gray-800 w-8 text-center">
                  {item.quantity}
                </span>
                
                <button
                  onClick={() => updateQuantity(item.id, 1)}
                  className="bg-gray-100 p-1 rounded-lg hover:bg-gray-200"
                >
                  <Plus size={16} className="text-gray-600" />
                </button>
                
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="ml-2 bg-red-50 p-1 rounded-lg hover:bg-red-100"
                >
                  <Trash2 size={16} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}

          {/* Coupon Section */}
          <div className="bg-white rounded-xl shadow-sm p-4 mt-4">
            {!appliedCoupon ? (
              <>
                <button
                  onClick={() => setShowCouponInput(!showCouponInput)}
                  className="flex items-center gap-2 text-blue-600 font-semibold mb-3"
                >
                  <Tag size={20} />
                  <span>Apply Coupon</span>
                </button>

                {showCouponInput && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleApplyCoupon}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all"
                      >
                        Apply
                      </button>
                    </div>

                    {couponError && (
                      <div className="text-red-500 text-sm flex items-center gap-1">
                        <X size={16} />
                        <span>{couponError}</span>
                      </div>
                    )}

                    {/* Suggested Coupons */}
                    {suggestedCoupons.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                          <Sparkles size={16} className="text-yellow-500" />
                          <span>Available Coupons</span>
                        </p>
                        <div className="space-y-2">
                          {suggestedCoupons.map((coupon) => (
                            <div
                              key={coupon.code}
                              onClick={() => {
                                setCouponCode(coupon.code);
                                handleApplyCoupon();
                              }}
                              className="border border-dashed border-blue-300 rounded-lg p-3 cursor-pointer hover:bg-blue-50 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-bold text-blue-600">{coupon.code}</p>
                                  <p className="text-xs text-gray-600">{coupon.description}</p>
                                </div>
                                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                                  {coupon.type === 'percentage' ? `${coupon.discount}%` : `₹${coupon.discount}`} OFF
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={20} className="text-green-600" />
                    <div>
                      <p className="font-bold text-green-700">{appliedCoupon.code}</p>
                      <p className="text-sm text-green-600">You saved ₹{couponDiscount}!</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Free Shipping Motivation Message */}
          {(() => {
            const cartTotal = getCartTotal() - (couponDiscount || 0);
            const remaining = appSettings.freeShippingThreshold - cartTotal;
            
            if (cartTotal >= appSettings.freeShippingThreshold) {
              return (
                <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-400 rounded-xl p-4 mt-6 flex items-center gap-3">
                  <div className="text-3xl">🎉</div>
                  <div className="flex-1">
                    <p className="font-bold text-green-700">You qualify for FREE shipping!</p>
                    <p className="text-sm text-green-600">No delivery charges on this order</p>
                  </div>
                </div>
              );
            } else if (remaining > 0) {
              return (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-400 rounded-xl p-4 mt-6 flex items-center gap-3">
                  <div className="text-3xl">🚚</div>
                  <div className="flex-1">
                    <p className="font-bold text-blue-700">Add ₹{remaining.toFixed(0)} more for FREE shipping!</p>
                    <p className="text-sm text-blue-600">Save ₹{appSettings.standardShippingFee} on delivery charges</p>
                  </div>
                </div>
              );
            }
          })()}

          <div className="bg-white rounded-xl shadow-sm p-4 mt-6">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">₹{getCartTotal()}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between mb-2">
                <span className="text-green-600">Coupon Discount ({appliedCoupon.code})</span>
                <span className="font-semibold text-green-600">-₹{couponDiscount}</span>
              </div>
            )}
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Shipping</span>
              <span className="font-semibold">
                {getCartTotal() >= appSettings.freeShippingThreshold ? (
                  <span className="text-green-600">Free</span>
                ) : (
                  `₹${appSettings.standardShippingFee}`
                )}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Tax (GST {appSettings.taxRate}%)</span>
              <span className="font-semibold">₹{(getCartTotal() * (appSettings.taxRate / 100)).toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-lg font-bold">Total</span>
                <span className="text-lg font-bold text-blue-600">₹{getFinalTotal ? getFinalTotal() : getCartTotal()}</span>
              </div>
            </div>

            <button 
              onClick={handleCheckout}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold mt-4 hover:from-blue-600 hover:to-blue-700 transition-all">
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartScreen;